/**
 * Historical Figure Image Library
 * Fetches and displays image data for historical figures with single-image scrolling
 */
import { BioImageModal } from "./BioImageModal.js";

export class HistoricalFigureImageLibrary {
    constructor() {
        this.apiBaseUrl = "/api/artwork/character";
        this.charactersList = [
            "Felix Klein",
            "François Viète",
            "Claude Shannon",
            "Marcus Aurelius",
            "Archimedes",
            "Alessandro Volta",
            "Eratosthenes",
            "Thomas Bayes",
            "George Boole",
            "William Playfair",
            "Diophantus",
            "Omar Khayyam",
            "Benjamin Franklin",
            "Brahmagupta",
            "Leonhard Euler",
            "Joseph-Louis Lagrange",
            "Grace Hopper",
            "Galileo Galilei",
            "Carl Friedrich Gauss",
            "Ada Lovelace",
            "Sigmund Freud",
            "Socrates",
            "Al-Khwarizmi",
            "Jacob Bernoulli",
            "Blaise Pascal",
            "Srinivasa Ramanujan",
            "Euclid",
            "Al Capone",
            "René Descartes",
            "Isaac Newton",
            "Oppenheimer",
            "Albert Einstein",
            "Confucius",
            "Cyrus the Great",
            "Genghis Khan",
            "George Orwell",
            "Henry Ford",
            "Leonardo da Vinci",
            "Malcolm X",
            "Mansa Musa",
            "Marie Curie",
            "Muhammad Ali",
            "Abraham Lincoln",
            "Niels Bohr",
            "Nikola Tesla",
            "Plato",
            "Richard Feynman",
            "James Clerk Maxwell",
            "Ernest Hemingway",
            "Charles Darwin",
            "Karl Marx",
            "Adam Smith",
            "Salvador Dali",
            "Van Gogh",
            "Warren Buffett",
            "Frida Kahlo",
            "Nostradamus",
            "Amelia Earhart",
            "Niccolo Machiavelli",
            "Enrico Fermi",
            "Friedrich Nietzsche",
        ];
        this.galleryContainer = document.getElementById("bio-gallery-images");
        this.indicators = document.getElementById("gallery-indicators");
        this.prevButton = document.getElementById("gallery-prev");
        this.nextButton = document.getElementById("gallery-next");
        this.currentCharacter = null;
        this.fetchedData = {};
        this.imageModal = null;
        this.currentIndex = 0; // Track current image index
        this.isDragging = false; // Track drag state
    }

    /**
     * Small helper to wait for a bit
     */
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Initialize the image library
     */
    init() {
        console.log("Initializing Historical Figure Image Library...");

        // Initialize the image modal
        this.imageModal = new BioImageModal();
        console.log("Bio image modal initialized");

        // Set up bio panel observer
        this.setupBioPanelObserver();

        // Listen for bio events
        this.listenForBioEvents();

        // Add navigation event listeners
        this.bindNavigationEvents();

        // Initialize drag-to-scroll
        this.initDragScroll();

        // Initialize keyboard navigation
        this.initKeyboardNavigation();

        console.log("Historical Figure Image Library initialized");
    }

    /**
     * Bind navigation button and indicator events
     */
    bindNavigationEvents() {
        if (this.prevButton) {
            this.prevButton.addEventListener("click", (e) => {
                e.preventDefault();
                this.navigateGallery("prev");
            });
            console.log("Previous button listener added");
        } else {
            console.log("Previous button not found");
        }

        if (this.nextButton) {
            this.nextButton.addEventListener("click", (e) => {
                e.preventDefault();
                this.navigateGallery("next");
            });
            console.log("Next button listener added");
        } else {
            console.log("Next button not found");
        }

        if (this.indicators) {
            this.indicators.addEventListener("click", (e) => {
                const indicator = e.target.closest(".bio-gallery-indicator");
                if (indicator) {
                    const index = parseInt(indicator.dataset.index);
                    this.navigateGalleryTo(index);
                }
            });
            console.log("Indicators listeners added");
        } else {
            console.log("Indicators not found");
        }
    }

    /**
     * Initialize drag-to-scroll functionality
     */
    initDragScroll() {
        if (!this.galleryContainer) return;

        let startX, scrollLeft;
        const gallery = this.galleryContainer;

        gallery.addEventListener("mousedown", (e) => {
            this.isDragging = true;
            gallery.classList.add("dragging");
            startX = e.pageX - gallery.offsetLeft;
            scrollLeft = gallery.scrollLeft;
            e.preventDefault();
        });

        gallery.addEventListener("mousemove", (e) => {
            if (!this.isDragging) return;
            e.preventDefault();
            const x = e.pageX - gallery.offsetLeft;
            const walk = (x - startX) * 2; // Adjust drag sensitivity
            gallery.scrollLeft = scrollLeft - walk;
        });

        gallery.addEventListener("mouseup", () => {
            this.isDragging = false;
            gallery.classList.remove("dragging");
            this.snapToNearestImage();
        });

        gallery.addEventListener("mouseleave", () => {
            if (this.isDragging) {
                this.isDragging = false;
                gallery.classList.remove("dragging");
                this.snapToNearestImage();
            }
        });

        // Touch support for mobile
        gallery.addEventListener("touchstart", (e) => {
            this.isDragging = true;
            gallery.classList.add("dragging");
            startX = e.touches[0].pageX - gallery.offsetLeft;
            scrollLeft = gallery.scrollLeft;
            e.preventDefault();
        });

        gallery.addEventListener("touchmove", (e) => {
            if (!this.isDragging) return;
            e.preventDefault();
            const x = e.touches[0].pageX - gallery.offsetLeft;
            const walk = (x - startX) * 2;
            gallery.scrollLeft = scrollLeft - walk;
        });

        gallery.addEventListener("touchend", () => {
            this.isDragging = false;
            gallery.classList.remove("dragging");
            this.snapToNearestImage();
        });

        console.log("Drag-to-scroll initialized");
    }

    /**
     * Snap to the nearest image after dragging
     */
    snapToNearestImage() {
        if (!this.galleryContainer) return;
        const imageWrappers = this.galleryContainer.querySelectorAll(
            ".bio-gallery-image-wrapper",
        );
        if (!imageWrappers.length) return;

        const imageWidth = imageWrappers[0]?.offsetWidth || 250;
        const imageGap = 15;
        const scrollLeft = this.galleryContainer.scrollLeft;
        const index = Math.round(scrollLeft / (imageWidth + imageGap));
        this.navigateGalleryTo(index);
    }

    /**
     * Initialize keyboard navigation
     */
    initKeyboardNavigation() {
        document.addEventListener("keydown", (e) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                this.navigateGallery("prev");
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                this.navigateGallery("next");
            } else if (e.key === "Escape") {
                // Close 3D image panel if it's open
                if (window.modelViewer && window.modelViewer.hideImage3D) {
                    window.modelViewer.hideImage3D();
                }
            }
        });
        console.log("Keyboard navigation initialized");
    }

    /**
     * Listen for events from the BioController
     */
    listenForBioEvents() {
        document.addEventListener("bioDataLoaded", (event) => {
            try {
                const bioData = event.detail;
                if (bioData?.name) {
                    console.log(`Bio data loaded for: ${bioData.name}`);
                    this.loadCharacterImages(bioData.name);
                }
            } catch (error) {
                console.error("Error handling bioDataLoaded event:", error);
            }
        });

        document.addEventListener("bioPanelOpened", (event) => {
            try {
                const characterName =
                    event.detail?.name || this.getCurrentCharacterFromDOM();
                if (characterName) {
                    console.log(`Bio panel opened for: ${characterName}`);
                    this.loadCharacterImages(characterName);
                }
            } catch (error) {
                console.error("Error handling bioPanelOpened event:", error);
            }
        });
    }

    /**
     * Get current character name from DOM or ModelManager
     * @returns {string|null}
     */
    getCurrentCharacterFromDOM() {
        try {
            if (window.ModelManager?.getCurrentModelId) {
                const modelId = window.ModelManager.getCurrentModelId();
                console.log(`Current model ID: ${modelId}`);
                const modelMap = {
                    "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": "Felix Klein",
                    "8f67d579-41c2-456d-900b-fccbcbf46bd3": "François Viète",
                    "e97568c5-1daa-4a95-b4ae-0fe374c6b083": "Marcus Aurelius",
                    "d13b5a81-ed77-4a25-8a01-a76a0165b349": "Archimedes",
                    "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": "Alessandro Volta",
                    "c73fed54-81e3-46ec-9d7c-3d11d05b142d": "Eratosthenes",
                    "3140d634-c7ca-4bda-af36-e1b49978b201": "Thomas Bayes",
                    "e3428624-bd1e-48b9-ae8c-c66ba68879cc": "George Boole",
                    "ca6ebdca-5151-4a58-8523-56d25fce5dfa": "William Playfair",
                    "78376372-3d71-4fce-8650-82b5be2c006b": "Diophantus",
                    "a2a43496-9a83-4444-813e-18a1930296e6": "Omar Khayyam",
                    "e617290b-bda0-4771-91ac-3fe6939dd412": "Benjamin Franklin",
                    "2707ca40-df64-4258-97c8-67d3695e8a42": "Brahmagupta",
                    "5a39bed2-a551-4a03-98a2-46104a3324bf": "Leonhard Euler",
                    "c93478a1-ed2f-4f1a-81f6-3fa474a21566": "Joseph-Louis Lagrange",
                    "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": "Grace Hopper",
                    "127cd993-f16e-4659-84fa-882cba633097": "Galileo Galilei",
                    "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": "Carl Friedrich Gauss",
                    "c0063a05-916b-45d0-bc2c-c63210414b17": "Ada Lovelace",
                    "3841a6bb-f977-453d-92f5-6cec1267546f": "Sigmund Freud",
                    "14836d13-ace6-4bf8-be26-26fbb80ed9bc": "Socrates",
                    "6bcf3678-e232-46c9-996a-67beb3fd2dbf": "Al-Khwarizmi",
                    "191e6103-a42c-4132-926c-230e7b1be5b6": "Jacob Bernoulli",
                    "5802e813-e715-491d-a0fd-92dc643c458e": "Blaise Pascal",
                    "f9f3f836-00c6-4160-8416-4b71b53f1307": "Srinivasa Ramanujan",
                    "7c7ac397-c36e-4218-92d4-f437d1959cca": "Euclid",
                    "e3f55c33-3ff0-471f-b833-b59d7b145fc6": "Al Capone",
                    "278dddd6-b4a8-4303-a41d-be6219dbc907": "Albert Einstein",
                    "b8951237-698b-42b5-8b6e-ee381b91fb65": "Confucius",
                    "9ef81498-3fb9-4175-a6d4-c2a02d71c640": "Cyrus the Great",
                    "581e7293-43cf-45ea-9fb3-0955c563cbe2": "Genghis Khan",
                    "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629": "George Orwell",
                    "5a16ff56-6e03-414e-a89d-cda9d83fae60": "Henry Ford",
                    "8944783c-2e2b-4796-b92e-21dacca776bc": "Leonardo da Vinci",
                    "9039a447-b6eb-4de2-9927-0b0c6476c090": "Malcolm X",
                    "3f14429d-b2d4-4b99-8a05-58eed567c0d3": "Mansa Musa",
                    "73f610bf-d9c9-4d9f-a026-a10086a66725": "Marie Curie",
                    "2baa0479-7e38-4473-a2f2-1ad54149dbac": "Muhammad Ali",
                    "802bd089-d231-4c4e-830c-476199321af8": "Niels Bohr",
                    "d537d61d-0313-421b-ae66-48a8d7222b3f": "Nikola Tesla",
                    "3542f19e-4e07-4828-8f9d-f330e4d6d660": "Plato",
                    "bc5d31a8-047b-4f72-8f16-fea7a365ca69": "Richard Feynman",
                    "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": "René Descartes",
                    "5eb1ad8d-4c11-4174-b79a-c232991d015b": "Oppenheimer",
                    "7021ffd6-51bb-41a8-920e-935899dcd631": "Isaac Newton",
                    "93ea7051-9722-443d-90fb-18a84c51894c":
                        "James Clerk Maxwell",
                    "b20dc26f-8924-4aee-9ef5-9b98c0367a34": "Ernest Hemingway",
                    "897eb00c-4f16-4f73-81d5-d1c91fad53f2": "Charles Darwin",
                    "d75e63e0-2b2b-43ef-933f-589e8d6818c0": "Salvador Dali",
                    "992e9007-b4f8-4711-80c2-884daf3d9960": "Van Gogh",
                    "44f87c0b-ca22-4225-9fea-8d823e6a0160": "Warren Buffett",
                    "4f50a698-6034-49c1-b9d4-221f59bf2378": "Frida Kahlo",
                    "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": "Nostradamus",
                    "031f2eea-64e5-441d-a37d-03d260cc43dc": "Abraham Lincoln",
                    "770e2d93-a6a4-41ed-aca1-8951327885b5": "Amelia Earhart",
                    "4d7d2e17-b818-4144-b78b-629c688a71ac": "Karl Marx",
                    "a59fe2c1-9046-405a-9a99-a7ddbaee13be": "Adam Smith",
                    "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b":
                        "Niccolo Machiavelli",
                    "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13": "Enrico Fermi",
                    "88c314c4-5890-4e55-9418-8b46a376ef81a":
                        "Friedrich Nietzsche",
                    "01db85dc-81e0-4433-9440-a54ce4976178":
                        "Erwin Schrodinger",
                    "2db1d398-0161-46cd-8aff-53008d29a9dc": "Claude Shannon",
                    "c2cc71ce-89e7-4c94-9441-eec86ec67a60":
                        "Napoleon Bonaparte",
                    "0c6099ed-c0db-4dd7-bc46-1d720324db5a": "Michael Faraday",
                    "1a35136c-42d8-46c7-a189-c4b32da87832": "John Neumann",
                    "be0dcf0f-c72b-48f7-a462-6ba7560498d7": "Alan Turing",
                    "c4b9ac58-a66a-451e-b035-afabd8be50b9": "Aristotle",
                    "d82bc58b-5487-4af2-ad5e-fa9fa838e239": "Charles Darwin",
                    "8f109c00-f7d2-4b9b-b44e-740a55e4339e":
                        "George Washington",
                    "d9f9af52-ae93-4bd2-abd4-0b7032844ddd": "Nikola Tesla",
                    "548fe879-e63c-4d6c-a377-525f4f9e12c8":
                        "Satoshi Nakamoto",
                };
                if (modelMap[modelId]) return modelMap[modelId];
            }

            const bioTitle = document.querySelector(".bio-panel-title");
            if (bioTitle?.textContent) {
                const titleText = bioTitle.textContent.trim();
                const nameMapping = {
                    "Richard Phillips Feynman": "Richard Feynman",
                    "Leonardo di ser Piero da Vinci": "Leonardo da Vinci",
                    "Marie Skłodowska Curie": "Marie Curie",
                    "Genghis Khan (born Temüjin)": "Genghis Khan",
                };
                if (nameMapping[titleText]) return nameMapping[titleText];
                for (const character of this.charactersList) {
                    if (titleText.includes(character)) return character;
                }
                return titleText;
            }

            console.log("Could not determine character name");
            return null;
        } catch (error) {
            console.error("Error getting character from DOM:", error);
            return null;
        }
    }

    /**
     * Set up observer for bio panel
     */
    setupBioPanelObserver() {
        const bioPanel = document.getElementById("bio-panel");
        if (!bioPanel) {
            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (
                        mutation.type === "childList" &&
                        mutation.addedNodes.length
                    ) {
                        const addedBioPanel = Array.from(
                            mutation.addedNodes,
                        ).find((node) => node.id === "bio-panel");
                        if (addedBioPanel) {
                            this.updateElementReferences();
                            const characterName =
                                this.getCurrentCharacterFromDOM();
                            if (characterName) {
                                this.loadCharacterImages(characterName);
                                document.dispatchEvent(
                                    new CustomEvent("bioPanelOpened", {
                                        detail: { name: characterName },
                                    }),
                                );
                            }
                            this.setupBioPanelVisibilityListener(addedBioPanel);
                        }
                    }
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
        } else {
            this.setupBioPanelVisibilityListener(bioPanel);
        }

        console.log("Bio panel visibility listener set up");
    }

    /**
     * Update element references after DOM changes
     */
    updateElementReferences() {
        this.galleryContainer = document.getElementById("bio-gallery-images");
        this.indicators = document.getElementById("gallery-indicators");
        this.prevButton = document.getElementById("gallery-prev");
        this.nextButton = document.getElementById("gallery-next");

        console.log("Element references updated:", {
            galleryContainer: !!this.galleryContainer,
            indicators: !!this.indicators,
            prevButton: !!this.prevButton,
            nextButton: !!this.nextButton,
        });
    }

    /**
     * Set up visibility listener for bio panel
     */
    setupBioPanelVisibilityListener(bioPanel) {
        const observer = new MutationObserver(() => {
            const isVisible =
                bioPanel.classList.contains("active") ||
                bioPanel.style.transform === "translateX(0%)" ||
                bioPanel.style.transform === "translateX(0px)";

            if (isVisible && !this.currentCharacter) {
                const characterName = this.getCurrentCharacterFromDOM();
                if (characterName) {
                    this.loadCharacterImages(characterName);
                }
            }
        });

        observer.observe(bioPanel, {
            attributes: true,
            attributeFilter: ["class", "style"],
        });
    }

    /**
     * Navigate gallery in specified direction
     */
    navigateGallery(direction) {
        if (!this.galleryContainer) return;

        const imageWrappers = this.galleryContainer.querySelectorAll(
            ".bio-gallery-image-wrapper",
        );
        if (!imageWrappers.length) return;

        const imageWidth = imageWrappers[0]?.offsetWidth || 250;
        const imageGap = 15;
        const totalWidth = imageWidth + imageGap;

        if (direction === "next") {
            this.currentIndex = Math.min(
                this.currentIndex + 1,
                imageWrappers.length - 1,
            );
        } else if (direction === "prev") {
            this.currentIndex = Math.max(this.currentIndex - 1, 0);
        }

        this.scrollToIndex(this.currentIndex, totalWidth);
        this.updateIndicators();
    }

    /**
     * Navigate gallery to specific index
     */
    navigateGalleryTo(index) {
        if (!this.galleryContainer) return;

        const imageWrappers = this.galleryContainer.querySelectorAll(
            ".bio-gallery-image-wrapper",
        );
        if (!imageWrappers.length) return;

        this.currentIndex = Math.max(
            0,
            Math.min(index, imageWrappers.length - 1),
        );

        const imageWidth = imageWrappers[0]?.offsetWidth || 250;
        const imageGap = 15;
        const totalWidth = imageWidth + imageGap;

        this.scrollToIndex(this.currentIndex, totalWidth);
        this.updateIndicators();
    }

    /**
     * Scroll to specific index
     */
    scrollToIndex(index, totalWidth) {
        const scrollPosition = index * totalWidth;
        this.galleryContainer.scrollTo({
            left: scrollPosition,
            behavior: "smooth",
        });
    }

    /**
     * Update indicator active state
     */
    updateIndicators() {
        if (!this.indicators) return;

        const indicators = this.indicators.querySelectorAll(
            ".bio-gallery-indicator",
        );
        indicators.forEach((indicator, index) => {
            indicator.classList.toggle("active", index === this.currentIndex);
        });
    }

    /**
     * Load character images and display them
     */
    async loadCharacterImages(characterName) {
        if (!characterName) return;

        // Only short-circuit when we've already loaded this character AND have
        // a non-empty cached result; otherwise allow a retry so a transient
        // empty/error response doesn't poison the rest of the session.
        if (this.currentCharacter === characterName) {
            const cached = this.fetchedData[characterName];
            if (Array.isArray(cached) && cached.length > 0) {
                return;
            }
        }

        try {
            this.currentCharacter = characterName;
            console.log(`Loading images for character: ${characterName}`);

            // Update element references in case DOM has changed
            this.updateElementReferences();

            if (!this.galleryContainer) {
                console.warn("Gallery container not found, retrying...");
                setTimeout(() => this.loadCharacterImages(characterName), 500);
                return;
            }

            const imageData = await this.fetchCharacterImages(characterName);
            if (imageData && imageData.length > 0) {
                this.displayImages(imageData);
                this.bindNavigationEvents(); // Re-bind navigation events
            } else {
                console.warn(`No images found for ${characterName}`);
                this.displayNoImagesMessage();
            }
        } catch (error) {
            console.error("Error loading character images:", error);
            this.displayErrorMessage();
        }
    }

    /**
     * Fetch character images from API with up to 5 attempts.
     * This helps with autoscale cold-start where first request often returns nothing.
     */
    async fetchCharacterImages(characterName) {
        // If we already have non-empty cache, use it
        const cached = this.fetchedData[characterName];
        if (Array.isArray(cached) && cached.length > 0) {
            return cached;
        }

        const maxAttempts = 5; // total attempts (1 + 4 retries)
        const retryDelayMs = 1200; // delay between attempts (tweak as needed)
        let lastResult = [];

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const url = `${this.apiBaseUrl}/${encodeURIComponent(
                    characterName,
                )}`;
                console.log(
                    `[Artwork] Attempt ${attempt}/${maxAttempts} for "${characterName}" → ${url}`,
                );

                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(
                        `HTTP ${response.status}: ${response.statusText}`,
                    );
                }

                const data = await response.json();

                if (data.status === "success" && data.models) {
                    const allImages = [];
                    const seenDescriptions = new Set();

                    data.models.forEach((model) => {
                        if (model.images && Array.isArray(model.images)) {
                            const description = model.description || "";
                            if (!seenDescriptions.has(description)) {
                                seenDescriptions.add(description);
                                allImages.push({
                                    url: model.images[0],
                                    description: description,
                                    searchTerm: model.search_term,
                                    enhanced_description:
                                        model.enhanced_description,
                                });
                            }
                        }
                    });

                    lastResult = allImages;

                    if (allImages.length > 0) {
                        console.log(
                            `[Artwork] Got ${allImages.length} images for "${characterName}" on attempt ${attempt}`,
                        );
                        // Cache and return immediately
                        this.fetchedData[characterName] = allImages;
                        return allImages;
                    } else {
                        console.warn(
                            `[Artwork] 0 images for "${characterName}" on attempt ${attempt}`,
                        );
                    }
                } else {
                    console.warn(
                        `[Artwork] Non-success or missing models for "${characterName}" on attempt ${attempt}:`,
                        data,
                    );
                }
            } catch (error) {
                console.error(
                    `[Artwork] Error on attempt ${attempt} for "${characterName}":`,
                    error,
                );
            }

            // If we’re not at the last attempt, wait and try again
            if (attempt < maxAttempts) {
                console.warn(
                    `[Artwork] Retrying "${characterName}" after ${retryDelayMs}ms (attempt ${attempt + 1}/${maxAttempts})...`,
                );
                await this.sleep(retryDelayMs);
            }
        }

        // Only cache non-empty results so a transient empty response doesn't
        // pin the gallery to placeholders for the rest of the session.
        if (lastResult.length > 0) {
            this.fetchedData[characterName] = lastResult;
        }
        return lastResult;
    }

    /**
     * Display images in the gallery
     */
    displayImages(images) {
        if (!this.galleryContainer) return;

        // Clear existing content
        this.galleryContainer.innerHTML = "";
        this.currentIndex = 0;

        // Create image elements
        images.forEach((imageData, index) => {
            const imageWrapper = document.createElement("div");
            imageWrapper.className = "bio-gallery-image-wrapper";
            imageWrapper.dataset.index = index;

            const img = document.createElement("img");
            img.src =
                imageData.url ||
                `/api/proxy-image?url=${encodeURIComponent(imageData.imageUrl)}`;
            img.alt =
                imageData.enhanced_description ||
                imageData.description ||
                "Historical artwork";
            img.className = "bio-gallery-image";
            img.loading = "lazy";

            // Add click event for modal - display image behind model and send description to chat
            img.addEventListener("click", () => {
                if (this.imageModal) {
                    const characterName = this.getCurrentCharacterFromDOM();
                    this.imageModal.showModal(
                        imageData.url,
                        imageData.description,
                        characterName,
                    );
                }
            });

            imageWrapper.appendChild(img);
            this.galleryContainer.appendChild(imageWrapper);
        });

        // Create indicators if container exists
        if (this.indicators) {
            this.createIndicators(images.length);
        }

        // Initialize navigation after images are loaded
        this.updateIndicators();

        console.log(`Displayed ${images.length} images in gallery`);
    }

    /**
     * Create navigation indicators
     */
    createIndicators(count) {
        if (!this.indicators) return;

        this.indicators.innerHTML = "";
        for (let i = 0; i < count; i++) {
            const indicator = document.createElement("button");
            indicator.className = "bio-gallery-indicator";
            indicator.dataset.index = i;
            indicator.setAttribute("aria-label", `Image ${i + 1}`);

            if (i === 0) indicator.classList.add("active");

            this.indicators.appendChild(indicator);
        }
    }

    /**
     * Display no images message
     */
    displayNoImagesMessage() {
        if (!this.galleryContainer) return;

        this.galleryContainer.innerHTML = `
            <div class="no-images-message">
                <p>No images available for this character.</p>
            </div>
        `;
    }

    /**
     * Display error message
     */
    displayErrorMessage() {
        if (!this.galleryContainer) return;

        this.galleryContainer.innerHTML = `
            <div class="error-message">
                <p>Error loading images. Please try again later.</p>
            </div>
        `;
    }
}
